import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
} from '@heroicons/react/solid';
import { Fragment } from 'react';
import { Popover, Transition } from '@headlessui/react';
import dayjs, { Dayjs } from 'dayjs';
import buildCalendar from './build';
import dayStyles from './styles';
import styled from 'styled-components';
import { WHITE_LABEL_ACCENT_COLOR } from '../../lib/white-label';

interface props {
  sel?: any;
  setSel: (e: any) => void;
  min?: Dayjs;
  max?: Dayjs;
}

const CalendarArrowButton = styled.button`
  &:focus {
    border-color: ${(props) => `${WHITE_LABEL_ACCENT_COLOR}`};
    border-width: 2px;
    box-shadow: none;
  }
`;

const CalendarInputButton = styled.button`
  width: 100%;
  button {
    &:focus {
      border-color: ${(props) => `${WHITE_LABEL_ACCENT_COLOR}`};
      border-width: 2px;
      box-shadow: none;
    }
  }
`;

const CalendarCellComponent = styled.div`
  button {
    border: none;
    outline: none;
    box-shadow: none;
    &:hover {
      background-color: ${(props) => WHITE_LABEL_ACCENT_COLOR};
      outline: none;
      border: none;
    }
  }
`;

export default function PurchaseRentalDateCal({
  sel,
  setSel,
  min,
  max,
}: props) {
  var badMutable = require('dayjs/plugin/badMutable');
  dayjs.extend(badMutable);
  var localizedFormat = require('dayjs/plugin/localizedFormat');
  dayjs.extend(localizedFormat);

  const buttonRef = useRef<any>();
  const panelRef = useRef<any>();

  const [month, setMonth] = useState(dayjs());
  const [calendar, setCalendar] = useState([]);

  function isSelected(day, sel): boolean {
    return sel?.isSame(day, 'day');
  }

  function isToday(day) {
    if (min) {
      // flag minimum day as starting point
      return day.isSame(min, 'day');
    } else {
      // flag current day as starting point
      return day.isSame(dayjs(), 'day');
    }
  }

  const isBeforeToday = useCallback(
    (day) => {
      if (min) {
        return day?.isBefore(min, 'day') || min?.isSame(day);
      } else {
        const current = dayjs();
        // console.log(
        //   day,
        //   current.diff(day, 'hours'),
        //   day?.isBefore(current, 'day'),
        //   current.diff(day, 'hours') > 12,
        // );
        return day?.isBefore(current, 'day') || current.diff(day, 'hours') > 12;
      }
    },
    [min],
  );

  const isAfterToday = useCallback(
    (day) => {
      if (max) {
        return day?.isAfter(max, 'day');
      } else {
        return day?.isAfter(dayjs(), 'day');
      }
    },
    [max],
  );

  useEffect(() => {
    // new year, next month
    if (sel.month() < month.month() && sel.year() > month.year()) {
      setMonth(nextMonth());
    }
    // prev year, prev month for new year
    else if (sel.month() > month.month() && sel.year() < month.year()) {
      setMonth(prevMonth());
    } else if (sel.month() > month.month() && sel.year() == month.year()) {
      setMonth(nextMonth());
    } else if (sel.month() < month.month() && sel.year() == month.year()) {
      setMonth(prevMonth());
    }
  }, [sel]);

  useEffect(() => {
    setCalendar(buildCalendar(month));
  }, [sel, month]);

  function classNames(...classes) {
    return classes.filter(Boolean).join(' ');
  }

  function prevMonth() {
    return month.clone().subtract(1, 'month');
  }

  function nextMonth() {
    return month.clone().add(1, 'month');
  }

  function thisMonth() {
    return month.isSame(new Date(), 'month');
  }

  const handleSetDate = useCallback(
    (day) => {
      setSel(day);
    },
    [setSel],
  );

  const [rendered, setRendered] = useState(false);
  useEffect(() => setRendered(true), []);

  if (!rendered) return;

  return (
    <Popover ref={panelRef} className="relative">
      {({ open }) => (
        <>
          <CalendarInputButton>
            <Popover.Button
              ref={buttonRef}
              className="text-gray-700 my-1 inline-flex w-full focus:z-10 items-center rounded-md border border-gray-300 shadow-sm px-2 py-2 bg-white text-sm font-medium hover:bg-gray-50 focus:border-blue-500 focus:border-2 focus:ring-opacity-50 focus:outline-none focus:ring-4 focus:ring-offset-gray-100 focus:ring-blue-500"
            >
              <CalendarIcon
                className={classNames(
                  open ? 'text-blue-400' : 'text-gray-400',
                  'mr-2 h-5 w-5 group-hover:text-gray-500',
                )}
                style={{
                  color: WHITE_LABEL_ACCENT_COLOR,
                }}
                aria-hidden="true"
              />
              <span>{sel?.format('L')}</span>
            </Popover.Button>
          </CalendarInputButton>
          <Transition
            as={Fragment}
            enter="transition ease-out duration-200"
            enterFrom="opacity-0 -translate-y-1"
            enterTo="opacity-100 translate-y-0"
            leave="transition ease-in duration-150"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-1"
          >
            <Popover.Panel className="md:min-w-max origin-top-left absolute left-0 mt-1 bg-white rounded-xl border border-gray-300 shadow-lg z-10">
              {/* Calendar */}
              <div className="flex flex-col mx-auto p-3">
                <div className="flex items-center justify-between">
                  <CalendarArrowButton
                    onClick={() => (thisMonth() ? '' : setMonth(prevMonth()))}
                    type="button"
                    className="shadow-sm relative inline-flex items-center px-1 py-1 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <span className="sr-only">Previous</span>
                    <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
                  </CalendarArrowButton>
                  <span className="text-base font-medium text-gray-600">
                    {month.format('MMMM')}
                  </span>
                  <CalendarArrowButton
                    onClick={() => setMonth(nextMonth())}
                    type="button"
                    className="shadow-sm relative inline-flex items-center px-1 py-1 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 focus:z-10 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <span className="sr-only">Next</span>
                    <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
                  </CalendarArrowButton>
                </div>
                <div className="grid grid-cols-7 pb-1 mt-3 justify-center gap-1">
                  {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map((d) => (
                    <div className="flex-1 w-9 rounded-md text-base justify-center mx-auto text-center font-base text-gray-400">
                      {d}
                    </div>
                  ))}
                </div>
                <div className="grid grid-rows-1 gap-1 mx-auto">
                  {calendar.map((week) => (
                    <div className="grid grid-cols-7 gap-1">
                      {week.map((day) => {
                        const disabled =
                          isBeforeToday(day) ||
                          (max && isAfterToday(day)) ||
                          false;
                        return (
                          <CalendarCellComponent>
                            <Popover.Button
                              disabled={disabled}
                              onClick={
                                !disabled ? () => handleSetDate(day) : () => {}
                              }
                              className={`flex flex-col md:w-12 w-10 h-10 md:h-12 items-center justify-center text-center rounded-lg ${dayStyles(
                                day,
                                sel,
                                month,
                                isToday,
                                max ? isAfterToday : null,
                                disabled,
                              )}`}
                              style={{
                                backgroundColor: isSelected(day, sel)
                                  ? WHITE_LABEL_ACCENT_COLOR
                                  : '',
                                boxShadow: 'none',
                              }}
                            >
                              <span className="flex justify-center content-center text-sm">
                                {day.format('D')}
                              </span>
                            </Popover.Button>
                          </CalendarCellComponent>
                        );
                      })}
                    </div>
                  ))}
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </>
      )}
    </Popover>
  );
}
